import React, { MouseEvent, useCallback, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';

import GeneratedContentPromptModal from '~/components/Common/GeneratedContentPromptModal';

import useToggleState from '~/hooks/useToggleState';

import { AdminOfferContentResponse, AdminPrompt } from '~/services/AccommodationService';
import { diffDays, formatDateLong, getTimeDifference } from '~/services/TimeService';

interface Props {
  content: AdminOfferContentResponse['generatedContents'];
  applyContent: (content: Partial<App.Offer>) => void;
  setOfferPrompt: (offerPrompt: AdminPrompt) => void;
}

const mapFieldNames = {
  description: 'description',
  nearby: 'getting_there',
  finePrint: 'fine_print',
};

const getSourceLabel = (
  contentSource: AdminOfferContentResponse['generatedContents'][0]['content'][0]['sources'][0],
) => {
  if (contentSource.type === 'bedbank_property') {
    return contentSource.type + ' - ' + contentSource.field;
  }
  return contentSource.type + ' - ' + contentSource.typeId + ' - ' + contentSource.field;
};

export default function AccommodationGeneratedContentView({ content, applyContent, setOfferPrompt }: Props) {
  // Initialize state once using useState
  const [currentFieldViewing, setCurrentFieldViewing] = useState<Record<string, number>>(() => {
    // Create initial state with all fields set to index 0
    return Array.isArray(content)
      ? content.reduce<Record<string, number>>((acc, field) => {
          acc[field.fields] = 0;
          return acc;
        }, {})
      : {};
  });

  const [selectedPrompt, setSelectedPrompt] = useState<any>(null);
  const {
    isToggled: isPromptModalOpen,
    toggleOn: setOpenPromptModal,
    toggleOff: setClosePromptModal,
  } = useToggleState(false);

  const handleBackClick = useCallback(
    (field: AdminOfferContentResponse['generatedContents'][0]) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentFieldViewing((prev) => ({
        ...prev,
        [field.fields]: Math.max(0, (prev[field.fields] || 0) - 1),
      }));
    },
    [],
  );

  const handleForwardClick = useCallback(
    (field: AdminOfferContentResponse['generatedContents'][0]) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentFieldViewing((prev) => ({
        ...prev,
        [field.fields]: Math.min(field.content.length - 1, (prev[field.fields] || 0) + 1),
      }));
    },
    [],
  );

  const handleApplyContent = useCallback(
    (content: Partial<App.Offer>) => {
      applyContent(content);
    },
    [applyContent],
  );

  const handleApplyAllContent = useCallback(() => {
    const allContent = {};
    content.forEach((field) => {
      const currentIndex = currentFieldViewing[field.fields] !== undefined ? currentFieldViewing[field.fields] : 0;
      allContent[mapFieldNames[field.fields]] = field.content[currentIndex].value;
    });
    applyContent(allContent);
  }, [applyContent, content, currentFieldViewing]);

  const handleShowPrompt = useCallback(
    (prompt: any) => {
      setSelectedPrompt(prompt);
      setOpenPromptModal();
    },
    [setOpenPromptModal],
  );

  const handleApplyFieldContent = useCallback(
    (field: string, value: string) => {
      handleApplyContent({ [mapFieldNames[field]]: value });
    },
    [handleApplyContent],
  );

  const handleShowPromptForField = useCallback(
    (field: AdminOfferContentResponse['generatedContents'][0], currentIndex: number) => {
      handleShowPrompt(field.content[currentIndex].prompt);
    },
    [handleShowPrompt],
  );

  const getTimeAgo = (date: string) => {
    return diffDays(new Date(date)) <= 10
      ? getTimeDifference(new Date(date), new Date(), true, true)
      : formatDateLong(new Date(date));
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Generated Content</Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            color="success"
            variant="contained"
            startIcon={<ContentPasteGoIcon />}
            onClick={handleApplyAllContent}
          >
            Use All
          </Button>
        </Box>
      </Box>
      {content.map((field, index) => {
        // Make sure we have a value for this field (handles new fields that might appear)
        const currentIndex = currentFieldViewing[field.fields] !== undefined ? currentFieldViewing[field.fields] : 0;

        return (
          <Accordion key={field.fields + '.' + index} defaultExpanded={field.fields !== 'finePrint'} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {field.fields}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button onClick={handleBackClick(field)}>
                    <ChevronLeftIcon />
                  </Button>
                  {currentIndex + 1} / {field.content.length}
                  <Button onClick={handleForwardClick(field)}>
                    <ChevronRightIcon />
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Generated {getTimeAgo(field.content[currentIndex].updatedAt)} ago
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', height: '400px', overflowY: 'auto', gap: 2 }}>
                <Stack direction="column" width="50%" height="100%" sx={{ p: 2 }} justifyContent="space-between">
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', width: '100%', maxHeight: '320px', overflowY: 'auto' }}
                  >
                    {field.content[currentIndex].value}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                      color="success"
                      variant="contained"
                      startIcon={<ContentPasteGoIcon />}
                      onClick={() => handleApplyFieldContent(field.fields, field.content[currentIndex].value)}
                    >
                      Use
                    </Button>
                  </Box>
                </Stack>
                <Box sx={{ width: '50%', p: 2, borderLeft: 1, borderColor: 'divider' }}>
                  <Stack sx={{ height: '100%' }}>
                    <Typography variant="h6">Sources</Typography>
                    <Box sx={{ mt: 1, overflow: 'auto', flexGrow: 1 }}>
                      {field.content[currentIndex].sources.map((source, sourceIndex) => (
                        <Accordion key={sourceIndex}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>{getSourceLabel(source)}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {source.value}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                    <Box mt={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="outlined" onClick={() => handleShowPromptForField(field, currentIndex)}>
                        Show Prompt
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {selectedPrompt && (
        <GeneratedContentPromptModal
          open={isPromptModalOpen}
          onClose={setClosePromptModal}
          prompt={selectedPrompt}
          onSetPrompt={setOfferPrompt}
        />
      )}
    </Box>
  );
}
